<template>
  <div class="selected-city-picker">
    <template v-if="isOnline">
      <div class="selected-city-picker__container">
        <div class="selected-city-picker__title">
          Укажите ваш город
        </div>

        <div v-click-outside="clickOutsideSelectCityPicker" class="selected-city-picker__select">
          <InputUI
            :value.sync="searchText"
            placeholder="Введите город, регион"
            class="selected-city-picker__input"
            @focus="onFocus"
          >
            <template slot="before">
            <span class="selected-city-picker__icon">
              <Icon icon="r-search" />
            </span>
            </template>
          </InputUI>

          <div
            v-if="showList"
            ref="searchList"
            class="selected-city-picker__list"
            @mouseenter="onMouseEnterListSearch"
            @mouseleave="onMouseLeaveListSearch"
          >
            <vue-custom-scrollbar
              class="selected-city-picker__list-scroll"
            >
              <div ref="searchListContent" class="selected-city-picker__list-content">
                <div
                  v-for="location in cityDadata"
                  :key="location.code"
                  class="item-selected-city-picker txt-m link-ui"
                >
                  <div
                    class="city-picker-popover__results-link button-clean"
                    @click.prevent="selectCity(location)"
                  >
                    <span class="city-picker-popover__results-link-title">{{ location.city }}</span>
                    <span class="city-picker-popover__results-link-subtext">{{ location.region }}</span>
                  </div>
                </div>
              </div>
            </vue-custom-scrollbar>
          </div>
        </div>
      </div>

      <div v-if="showFooter" class="selected-city-picker__footer">
        <button
          type="button"
          class="btn-ui btn-ui--full-mob"
          :class="{'disabled': !codeCity}"
          @click="setCity"
        >
          Выбрать
        </button>
      </div>
    </template>

    <template v-else>
      <div class="city-picker-popover__title">
        Соединение с интернетом отсутствует, поэтому изменить город нельзя
      </div>
    </template>
  </div>
</template>

<script>
import Vue from 'vue';
import { debounce } from 'lodash';
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import vueCustomScrollbar from 'vue-custom-scrollbar';
import vueClickOutside from '../../../utils/vue-click-outside';
import { mainApi } from '../../../api';
import { sendEventYM } from '../../../utils/common';
import { parseConfig } from '../../../../utils/json-config';
import Icon from '../common/Icon.vue';
import InputUI from '../common/InputUI.vue';

Vue.directive('click-outside', vueClickOutside);

export default {
  name: 'CityPickerPopover',

  components: {
    vueCustomScrollbar,
    InputUI,
    Icon
  },

  props: {
    typeDevice: {
      type    : String,
      default : ''
    }
  },

  data() {
    return {
      searchText   : '',
      cityDadata   : [],
      codeCity     : null,
      isFocus      : false,
      activeSearch : false,
      isOnline     : true,

      requestListCitiesAbortController: null
    };
  },

  watch: {
    searchText(val) {
      // Проверяем, что поиск подсказок активен
      if (!this.activeSearch) {
        this.activeSearch = true;
        return;
      }

      // Активируем фокус на поиске
      this.isFocus = true;
      // Сбрасываем значение id города
      this.codeCity = null;

      if (!!val) {
        this.getListCity.call(this, null, val);
      }
    }
  },

  computed: {
    showList() {
      return this.isFocus && this.searchText !== '' && this.cityDadata && this.cityDadata.length;
    },
    showFooter() {
      return this.typeDevice === 'mobile' || (this.typeDevice !== 'mobile' && this.codeCity && !this.isFocus);
    }
  },

  async created() {
    window.addEventListener('online', this.handleOfflineStatus);
    window.addEventListener('offline', this.handleOfflineStatus);

    this.handleOfflineStatus();
  },

  destroyed() {
    window.addEventListener('online', this.handleOfflineStatus);
    window.addEventListener('offline', this.handleOfflineStatus);
  },

  methods: {
    getListCity: debounce(async function (loading, query) {
      try {
        if (this.requestListCitiesAbortController) {
          this.requestListCitiesAbortController.abort();
        }
        this.requestListCitiesAbortController = new AbortController();

        const { sessid } = parseConfig();
        this.cityDadata = await mainApi.modules.selectCity.getListCity({ query, sessid });
      } catch {}
    }, 300),

    selectCity(location) {
      this.codeCity = location.code;
      this.searchText = `${location.city} ${location.region}`;

      this.activeSearch = false;
      this.isFocus = false;
    },

    setCity() {
      if (!this.codeCity) return;

      document.dispatchEvent(new Event('loader:show'));
      mainApi.modules.selectCity.setCity({ code: this.codeCity })
        .then(() => {
          sendEventYM('change_city');
          window.location.reload();
        })
        .catch(() => {
          document.dispatchEvent(new Event('loader:hide'));
        });
    },

    onFocus() {
      this.isFocus = true;
    },

    clickOutsideSelectCityPicker() {
      this.isFocus = false;
    },

    onMouseEnterListSearch() {
      const isActiveScroll = this.$refs.searchList && this.$refs.searchListContent
        ? this.$refs.searchList.getBoundingClientRect().height < this.$refs.searchListContent.getBoundingClientRect().height
        : false;

      // Блокируем скролл страницы, пока не уйдём со списка
      // при условии что прокрутка есть
      if (isActiveScroll) {
        disableBodyScroll(this.$el, {
          reserveScrollBarGap: true
        });
      }
    },

    onMouseLeaveListSearch() {
      // Разблокируем скролл страницы, когда уходим со списка
      enableBodyScroll(this.$el);
    },

    handleOfflineStatus() {
      this.isOnline = navigator.onLine;
    }
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../css/redesign/utils/variables";
@import "../../../../../css/redesign/utils/grid";

.selected-city-picker {
  --height-page: min(calc(var(--window-height) * 1px), 100vh);
  --max-container-mob: calc(var(--height-page) - #{$margin-popup-mob} - 72px - 24px - 24px);
  --max-scroll-mob: calc(var(--max-container-mob) - 106px);

  @include media-breakpoint-down(xsl) {
    padding-top: 8px;
  }
  &__container {
    @include media-breakpoint-down(xsl) {
      min-height: 220px;
      height: 432px;
      max-height: var(--max-container-mob);
    }
  }
  &__title {
    font-size: 20px;
    font-weight: 500;
    line-height: 1;
    letter-spacing: -1px;
    color: $c-blue-common;
  }
  &__input {
    margin-top: 24px;
  }
  &__icon {
    position: absolute;
    z-index: 2;
    top: 50%;
    left: 16px;
    width: 14px;
    height: 14px;
    line-height: 1;
    color: $txt-dark-grey;
    pointer-events: none;
    transform: translateY(-50%);
  }
  &__list {
    margin-top: 8px;
    border-radius: 12px;
    box-shadow: 0 6px 16px 0 rgba(0, 53, 149, 0.08);
    background-color: $c-white;
  }
  &__list-scroll {
    position: relative;
    width: 100%;
    max-height: Min(var(--max-scroll-mob), 224px);
    flex-shrink: 0;

    @include media-breakpoint-down(xsl) {
      min-height: 56px;
    }
    @include media-breakpoint-up(sm) {
      max-height: 168px;
    }
  }
  &__footer {
    position: relative;
    margin-top: 24px;

    @include media-breakpoint-down(xsl) {
      padding-top: 16px;
    }
    &:after {
      @include media-breakpoint-down(xsl) {
        content: "";
        display: block;
        position: absolute;
        top: 0;
        left: -16px;
        right: -16px;
        height: 1px;
        background-color: $c-gray-on-wite;
      }
    }
  }
}

.item-selected-city-picker {
  display: block;
  padding: 20px 16px;
}
</style>

<style lang="scss">
@import "../../../../../css/redesign/utils/variables";

.selected-city-picker {
  .ps__rail-y {
    opacity: 1;
    width: 4px !important;
    background-color: transparent !important;
  }
  .ps__thumb-y {
    width: 4px !important;
    border-radius: 24px !important;
    background: $c-gray-on-wite !important;
  }
  &__input {
    .input-ui__input {
      padding-left: 40px !important;
    }
    .input-ui__label {
      padding-left: 40px !important;

      &.small {
        left: 0 !important;
        padding-left: 0;
      }
    }
  }
}
</style>
